import { CREATIVE_TEMPLATE_MAX_NODES } from "../../../utils/constants";
import { persistableCrop } from "../editor/layerCrop";
import { CANVAS_CLIPBOARD_KIND } from "./canvasClipboard";
import {
  canHighlightTextSource,
  scaleHighlightMetrics,
} from "./textHighlights";
import { listStoredInlineReplacements } from "./textInlineReplacements";
import { isVerticalTextOrientation } from "./textOrientation";

export {
  getArtboardVariableMismatches,
  listNodeVariables,
  syncVariablesFromArtboards,
  syncVariablesFromNodes,
} from "./nodeVariables.js";

export const ORIGIN_COLORS = {
  generated: "#7C3AED",
  data: "#2563EB",
  computed: "#16A34A",
  asset: "#52525B",
  fixed: "#52525B",
};

export const getOriginColor = ({ sourceKind, type }) => {
  if (type === "group" || type === "shape") return ORIGIN_COLORS.asset;
  return ORIGIN_COLORS[sourceKind] || ORIGIN_COLORS.asset;
};

export const slugifyName = ({ name, fallback = "layer" }) => {
  const slug = String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return slug || fallback;
};

export const uniqueName = ({ name, existingNames = [] }) => {
  let next = name;
  let suffix = 2;
  while (existingNames.includes(next)) {
    next = `${name}_${suffix}`;
    suffix += 1;
  }
  return next;
};

export const listNodeNames = ({ nodes = {} }) => {
  const names = [];
  Object.values(nodes).forEach((node) => {
    names.push(node.name);
    names.push(...listNodeNames({ nodes: node.children || {} }));
  });
  return names;
};

export const countNodes = ({ nodes = {} }) =>
  Object.values(nodes).reduce((total, node) => {
    return total + 1 + countNodes({ nodes: node.children || {} });
  }, 0);

const isPostprocessImageNode = (node) => {
  if (node?.type !== "image") return false;
  const kind = node?.source?.kind;
  if (kind === "data") return true;
  return Boolean(node?.postprocess) && kind === "asset";
};

export const treeHasPostprocess = ({ nodes = {} } = {}) =>
  Object.values(nodes || {}).some(
    (node) =>
      isPostprocessImageNode(node) ||
      treeHasPostprocess({ nodes: node?.children || {} })
  );

export const canProduceCreativeTemplate = ({ nodeCount, nodes, name } = {}) => {
  const hasName = String(name || "").trim().length > 0;
  const count =
    nodeCount != null ? Number(nodeCount) : countNodes({ nodes: nodes || {} });
  return hasName && count > 0;
};

export const normalizeChildOrder = ({ childOrder, children = {} }) => {
  if (Array.isArray(childOrder) && childOrder.length) return childOrder;
  if (childOrder && typeof childOrder === "object") {
    return Object.keys(childOrder)
      .sort((left, right) => Number(left) - Number(right))
      .map((key) => childOrder[key])
      .filter(Boolean);
  }
  return Object.keys(children);
};

export const findNodePath = ({ nodes = {}, nodeID, parentID = null }) => {
  const node = nodes[nodeID];
  if (node) {
    return { node, parentID, siblings: nodes };
  }
  const entries = Object.values(nodes);
  for (let index = 0; index < entries.length; index += 1) {
    const current = entries[index];
    const nested = findNodePath({
      nodes: current.children || {},
      nodeID,
      parentID: current.id,
    });
    if (nested) return nested;
  }
  return null;
};

export const resolveInsertParentID = ({ nodes = {}, selectedID }) => {
  if (!selectedID) return null;
  const found = findNodePath({ nodes, nodeID: selectedID });
  if (!found) return null;
  if (found.node?.type === "group") return found.node.id;
  return found.parentID || null;
};

const omitUndefinedDeep = (value) => {
  if (Array.isArray(value)) return value.map(omitUndefinedDeep);
  if (!value || typeof value !== "object") return value;
  const next = {};
  Object.entries(value).forEach(([key, item]) => {
    if (item === undefined) return;
    next[key] = omitUndefinedDeep(item);
  });
  return next;
};

export const patchNodeById = ({ nodes = {}, nodeID, patch }) => {
  const next = {};
  Object.entries(nodes).forEach(([id, node]) => {
    if (id === nodeID) {
      next[id] = omitUndefinedDeep({ ...node, ...patch });
      return;
    }
    next[id] = {
      ...node,
      children: node.children
        ? patchNodeById({ nodes: node.children, nodeID, patch })
        : node.children,
    };
  });
  return next;
};

export const deleteNodeById = ({ nodes = {}, nodeLayouts = {}, nodeID }) => {
  if (nodes[nodeID]) {
    const nextNodes = { ...nodes };
    delete nextNodes[nodeID];
    const nextLayouts = { ...nodeLayouts };
    delete nextLayouts[nodeID];
    return { nodes: nextNodes, nodeLayouts: nextLayouts };
  }
  const nextNodes = {};
  Object.entries(nodes).forEach(([id, node]) => {
    if (!node.children?.[nodeID]) {
      nextNodes[id] = {
        ...node,
        children: node.children
          ? deleteNodeById({
              nodes: node.children,
              nodeLayouts: {},
              nodeID,
            }).nodes
          : node.children,
      };
      return;
    }
    const children = { ...node.children };
    delete children[nodeID];
    nextNodes[id] = {
      ...node,
      children,
      childOrder: normalizeChildOrder({
        childOrder: node.childOrder,
        children,
      }).filter((childID) => childID !== nodeID),
    };
  });
  return { nodes: nextNodes, nodeLayouts };
};

export const deleteNodesByIds = ({
  nodes = {},
  nodeLayouts = {},
  nodeIDs = [],
}) =>
  nodeIDs.reduce(
    (graph, nodeID) =>
      findNodePath({ nodes: graph.nodes, nodeID })
        ? deleteNodeById({ ...graph, nodeID })
        : graph,
    { nodes, nodeLayouts }
  );

const defaultSourceForType = ({ type }) => {
  if (type === "text") {
    return { kind: "fixed", value: "" };
  }
  if (type === "image") {
    return { kind: "asset", assetRef: null };
  }
  return undefined;
};

export const createDefaultNode = ({
  id,
  name,
  existingNames = [],
  type = "text",
}) => {
  const unique = uniqueName({ name, existingNames });
  const node = {
    id,
    name: unique,
    type,
  };
  if (type === "group") {
    node.children = {};
    node.childOrder = [];
    node.componentFrame = { width: 200, height: 200 };
    node.clipContent = false;
    return node;
  }
  if (type === "shape") {
    node.shape = {
      kind: "roundedRectangle",
      radius: 8,
      fill: "#46248C",
    };
    return node;
  }
  node.source = defaultSourceForType({ type });
  if (type === "image") node.fit = "fill";
  if (type === "text") {
    node.style = {
      fontSize: 60,
      color: "#FFFFFF",
      align: "left",
      orientation: "horizontal",
      textStyle: "normal",
    };
    node.overflow = { strategy: "shrink" };
  }
  return node;
};

export const applyNodeType = ({ node, type, layout }) => {
  const next = createDefaultNode({
    id: node.id,
    name: node.name,
    existingNames: [],
    type,
  });
  next.name = node.name;
  if (type === "group") {
    next.componentFrame = {
      width: layout?.width || node.componentFrame?.width || 200,
      height: layout?.height || node.componentFrame?.height || 200,
    };
    next.children = node.type === "group" ? node.children || {} : {};
    next.childOrder =
      node.type === "group"
        ? normalizeChildOrder({
            childOrder: node.childOrder,
            children: next.children,
          })
        : [];
  } else {
    next.children = undefined;
    next.childOrder = undefined;
    next.componentFrame = undefined;
    next.clipContent = undefined;
  }
  if (type === "group" || type === "shape") next.source = undefined;
  if (type !== "shape") next.shape = undefined;
  if (type !== "text") {
    next.style = undefined;
    next.overflow = undefined;
  } else {
    next.overflow = { ...(node.overflow || {}), strategy: "shrink" };
  }
  if (type === "image") next.fit = "fill";
  else next.fit = undefined;
  next.crop = undefined;
  return next;
};

export const canBeCanvasBackground = ({ node, parentID } = {}) =>
  parentID == null &&
  node?.type === "image" &&
  (node?.source?.kind === "generated" || node?.source?.kind === "asset");

export const isCanvasBackgroundNode = ({ node, parentID } = {}) =>
  canBeCanvasBackground({ node, parentID }) && Boolean(node?.isBackground);

export const canvasBackgroundFit = (node) =>
  node?.source?.kind === "generated" ? "cover" : "fill";

const stripCanvasBackground = (node) => {
  if (!node?.isBackground) return node;
  const next = { ...node };
  delete next.isBackground;
  if (next.type === "image") next.fit = "fill";
  return next;
};

export const applyCanvasBackgroundLayout = ({
  nodeLayouts = {},
  nodeID,
  width,
  height,
}) => ({
  ...nodeLayouts,
  [nodeID]: {
    ...(nodeLayouts[nodeID] || {}),
    x: 0,
    y: 0,
    width: Math.round(Number(width) || 0),
    height: Math.round(Number(height) || 0),
    zIndex: 0,
    visible: true,
    rotation: 0,
  },
});

export const setRootCanvasBackground = ({
  nodes = {},
  nodeID,
  isBackground,
}) => {
  const next = {};
  Object.entries(nodes).forEach(([id, node]) => {
    if (!node) return;
    if (id === nodeID) {
      if (isBackground && canBeCanvasBackground({ node, parentID: null })) {
        next[id] = {
          ...node,
          isBackground: true,
          fit: canvasBackgroundFit(node),
        };
        return;
      }
      next[id] = stripCanvasBackground(node);
      return;
    }
    if (isBackground && node.isBackground) {
      next[id] = stripCanvasBackground(node);
      return;
    }
    next[id] = node;
  });
  return next;
};

export const ORIGIN_GHOST_OPACITY = 0.4;
export const IMAGE_ORIGIN_GHOST_OPACITY = ORIGIN_GHOST_OPACITY;

export const imageOriginPatch = ({ assetRef } = {}) => {
  const nextRef = String(assetRef || "").trim();
  if (!nextRef) return {};
  return { originAssetRef: nextRef };
};

export const textOriginPatch = ({ value } = {}) => {
  const nextValue = String(value ?? "");
  if (!nextValue) return { originText: undefined };
  return { originText: nextValue };
};

export const isTextOriginPreviewKind = (kind) =>
  kind === "data" || kind === "generated" || kind === "computed";

const lastStaticAssetRef = (node) => {
  const currentAsset =
    node?.source?.kind === "asset"
      ? String(node?.source?.assetRef || "").trim()
      : "";
  return currentAsset || String(node?.originAssetRef || "").trim();
};

const lastStaticText = (node) => {
  const currentText =
    node?.source?.kind === "fixed" ? String(node?.source?.value || "") : "";
  return currentText || String(node?.originText || "");
};

const attachImageOrigin = ({ node, next }) => {
  if (next?.type !== "image") return next;
  const originAssetRef = lastStaticAssetRef(node);
  if (!originAssetRef) return next;
  const withOrigin = { ...next, originAssetRef };
  delete withOrigin.originGhost;
  return withOrigin;
};

const attachTextOrigin = ({ node, next }) => {
  if (next?.type !== "text") return next;
  const originText = lastStaticText(node);
  if (!originText) return next;
  return { ...next, originText };
};

const attachOriginPreview = ({ node, next }) =>
  attachTextOrigin({
    node,
    next: attachImageOrigin({ node, next }),
  });

export const resolveImageLayerPreview = ({ box, assets = {} } = {}) => {
  const isStatic = box?.type === "image" && box?.sourceKind === "asset";
  const previewRef = isStatic
    ? String(box?.assetRef || box?.originAssetRef || "").trim()
    : String(box?.originAssetRef || "").trim();
  const assetUrl = previewRef ? assets[previewRef]?.url || "" : "";
  return {
    assetUrl,
    assetOpacity: assetUrl && !isStatic ? ORIGIN_GHOST_OPACITY : 1,
  };
};

export const resolveTextLayerPreview = ({ box } = {}) => {
  if (box?.type !== "text") return { text: "", textOpacity: 1 };
  const isStatic = box.sourceKind === "fixed";
  const previewText = isStatic
    ? String(box.text || box.originText || "")
    : String(box.originText || box.text || "");
  return {
    text: previewText,
    textOpacity: previewText && !isStatic ? ORIGIN_GHOST_OPACITY : 1,
  };
};

export const applySourceKind = ({ node, kind }) => {
  if (node.type === "group" || node.type === "shape") {
    const next = { ...node };
    delete next.source;
    return stripCanvasBackground(next);
  }
  if (
    node.type === "text" &&
    !["fixed", "data", "computed", "generated"].includes(kind)
  ) {
    return node;
  }
  if (node.type === "image" && !["asset", "data", "generated"].includes(kind)) {
    return node;
  }
  let next = node;
  if (kind === "fixed") {
    next = { ...node, source: { kind, value: lastStaticText(node) } };
  } else if (kind === "asset") {
    next = {
      ...node,
      source: {
        kind,
        assetRef: lastStaticAssetRef(node) || null,
      },
    };
  } else if (kind === "data") {
    const variable = slugifyName({ name: node.name, fallback: node.id });
    next = {
      ...node,
      name: variable,
      source: {
        kind,
        variable,
      },
    };
  } else if (kind === "computed") {
    next = {
      ...node,
      source: { kind, expression: node.source?.expression || "" },
    };
  } else {
    next = {
      ...node,
      source: {
        kind: "generated",
        generator: node.type === "text" ? "text" : "image",
        promptTemplate: node.source?.promptTemplate || "",
      },
    };
  }
  next = attachOriginPreview({ node, next });
  if (
    next.type === "text" &&
    next.style?.highlights &&
    !canHighlightTextSource(next.source?.kind)
  ) {
    const style = { ...next.style };
    delete style.highlights;
    next = { ...next, style };
  }
  if (!canBeCanvasBackground({ node: next, parentID: null })) {
    next = stripCanvasBackground(next);
  }
  if (
    next.postprocess &&
    !(next.type === "image" && ["asset", "data"].includes(next.source?.kind))
  ) {
    const stripped = { ...next };
    delete stripped.postprocess;
    return stripped;
  }
  return next;
};

export const applySourceKindByName = ({ nodes = {}, name, kind }) => {
  if (!name) return nodes;
  const next = {};
  Object.entries(nodes || {}).forEach(([id, node]) => {
    if (!node) return;
    const current = node.name === name ? applySourceKind({ node, kind }) : node;
    next[id] = current.children
      ? {
          ...current,
          children: applySourceKindByName({
            nodes: current.children,
            name,
            kind,
          }),
        }
      : current;
  });
  return next;
};

const pxBox = ({ nx, ny, nw, nh, width, height }) => ({
  x: Math.round(nx * width),
  y: Math.round(ny * height),
  width: Math.round(nw * width),
  height: Math.round(nh * height),
});

export const getAbsolutePx = ({
  nodes,
  nodeLayouts,
  nodeID,
  artboardWidth,
  artboardHeight,
}) => {
  const found = findNodePath({ nodes, nodeID });
  if (!found) return null;
  if (!found.parentID) {
    const layout = nodeLayouts[nodeID] || {};
    return {
      x: layout.x || 0,
      y: layout.y || 0,
      width: layout.width || 0,
      height: layout.height || 0,
      visible: layout.visible !== false,
      zIndex: layout.zIndex || 0,
      rotation: Number(layout.rotation) || 0,
    };
  }
  const parentBox = getAbsolutePx({
    nodes,
    nodeLayouts,
    nodeID: found.parentID,
    artboardWidth,
    artboardHeight,
  });
  const layout = found.node.layout || {};
  return {
    x: (parentBox?.x || 0) + (layout.x || 0),
    y: (parentBox?.y || 0) + (layout.y || 0),
    width: layout.width || 0,
    height: layout.height || 0,
    visible: layout.visible !== false && parentBox?.visible !== false,
    zIndex: layout.zIndex || 0,
    rotation: Number(layout.rotation) || 0,
  };
};

const compareRootPaintOrder = ({ left, right, nodeLayouts = {} }) => {
  const leftLayout = nodeLayouts[left] || {};
  const rightLayout = nodeLayouts[right] || {};
  const zDiff = (leftLayout.zIndex || 0) - (rightLayout.zIndex || 0);
  if (zDiff !== 0) return zDiff;
  const leftArea = (leftLayout.width || 0) * (leftLayout.height || 0);
  const rightArea = (rightLayout.width || 0) * (rightLayout.height || 0);
  return rightArea - leftArea;
};

export const getRootOrder = ({ nodes = {}, nodeLayouts = {} }) =>
  Object.keys(nodes).sort((left, right) =>
    compareRootPaintOrder({ left, right, nodeLayouts })
  );

export const flattenTreeToBoxes = ({
  nodes = {},
  nodeLayouts = {},
  artboardWidth,
  artboardHeight,
}) => {
  if (!artboardWidth || !artboardHeight) return [];
  const boxes = [];
  const walk = ({ map, parentID }) => {
    const ordered = parentID
      ? normalizeChildOrder({
          childOrder: findNodePath({ nodes, nodeID: parentID })?.node
            ?.childOrder,
          children: map,
        })
      : getRootOrder({ nodes: map, nodeLayouts });
    ordered.forEach((id) => {
      const node = map[id];
      if (!node) return;
      const abs = getAbsolutePx({
        nodes,
        nodeLayouts,
        nodeID: id,
        artboardWidth,
        artboardHeight,
      });
      if (!abs || abs.visible === false) return;
      boxes.push({
        id: node.id,
        name: node.name,
        type: node.type,
        sourceKind: node.source?.kind,
        assetRef: node.source?.assetRef || "",
        originAssetRef: node.originAssetRef || "",
        originText: node.originText || "",
        text: node.type === "text" ? lastStaticText(node) : "",
        style: node.style || null,
        overflow: node.overflow || null,
        fit: node.fit || "fill",
        crop: persistableCrop(node.crop),
        parentID,
        isBackground: isCanvasBackgroundNode({ node, parentID }),
        locked: Boolean(node.locked),
        nx: abs.x / artboardWidth,
        ny: abs.y / artboardHeight,
        nw: abs.width / artboardWidth,
        nh: abs.height / artboardHeight,
        rotation: Number(abs.rotation) || 0,
        zIndex: abs.zIndex || 0,
        paintIndex: boxes.length,
      });
      walk({ map: node.children || {}, parentID: id });
    });
  };
  walk({ map: nodes, parentID: null });
  return boxes;
};

export const applyOverlayBox = ({
  nodes,
  nodeLayouts,
  nodeID,
  nx,
  ny,
  nw,
  nh,
  rotation,
  crop,
  artboardWidth,
  artboardHeight,
}) => {
  const abs = pxBox({
    nx,
    ny,
    nw,
    nh,
    width: artboardWidth,
    height: artboardHeight,
  });
  const found = findNodePath({ nodes, nodeID });
  if (!found) return { nodes, nodeLayouts };
  if (isCanvasBackgroundNode({ node: found.node, parentID: found.parentID })) {
    return {
      nodes,
      nodeLayouts: applyCanvasBackgroundLayout({
        nodeLayouts,
        nodeID,
        width: artboardWidth,
        height: artboardHeight,
      }),
    };
  }
  const nextRotation =
    rotation === undefined ? undefined : Number(rotation) || 0;
  const cropPatch = crop === undefined ? {} : { crop: persistableCrop(crop) };
  if (!found.parentID) {
    return {
      nodes: Object.keys(cropPatch).length
        ? patchNodeById({ nodes, nodeID, patch: cropPatch })
        : nodes,
      nodeLayouts: {
        ...nodeLayouts,
        [nodeID]: {
          ...(nodeLayouts[nodeID] || {}),
          ...abs,
          ...(nextRotation === undefined ? {} : { rotation: nextRotation }),
        },
      },
    };
  }
  const parentBox = getAbsolutePx({
    nodes,
    nodeLayouts,
    nodeID: found.parentID,
    artboardWidth,
    artboardHeight,
  });
  return {
    nodeLayouts,
    nodes: patchNodeById({
      nodes,
      nodeID,
      patch: {
        layout: {
          ...(found.node.layout || {}),
          x: abs.x - (parentBox?.x || 0),
          y: abs.y - (parentBox?.y || 0),
          width: abs.width,
          height: abs.height,
          ...(nextRotation === undefined ? {} : { rotation: nextRotation }),
        },
        ...cropPatch,
      },
    }),
  };
};

export const applyOverlayBoxes = ({
  nodes,
  nodeLayouts,
  updates = [],
  artboardWidth,
  artboardHeight,
}) =>
  (Array.isArray(updates) ? updates : []).reduce(
    (acc, update) =>
      applyOverlayBox({
        nodes: acc.nodes,
        nodeLayouts: acc.nodeLayouts,
        nodeID: update.id,
        nx: update.nx,
        ny: update.ny,
        nw: update.nw,
        nh: update.nh,
        rotation: update.rotation,
        crop: update.crop,
        artboardWidth,
        artboardHeight,
      }),
    { nodes, nodeLayouts }
  );

const nextHighestZIndex = (zIndexes = []) =>
  zIndexes.reduce((maxZ, z) => Math.max(maxZ, Number(z) || 0), 0) + 1;

const nextRootInsertZIndex = (nodeLayouts = {}) =>
  nextHighestZIndex(Object.values(nodeLayouts).map((layout) => layout?.zIndex));

const nextGroupInsertZIndex = (children = {}) =>
  nextHighestZIndex(
    Object.values(children).map((child) => child?.layout?.zIndex)
  );

const getRotatedBoxCorners = ({ x, y, width, height, rotation = 0 }) => {
  const boxWidth = Math.max(0, Number(width) || 0);
  const boxHeight = Math.max(0, Number(height) || 0);
  const boxX = Number(x) || 0;
  const boxY = Number(y) || 0;
  const radians = ((Number(rotation) || 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const centerX = boxX + boxWidth / 2;
  const centerY = boxY + boxHeight / 2;
  const halfWidth = boxWidth / 2;
  const halfHeight = boxHeight / 2;
  return [
    [-halfWidth, -halfHeight],
    [halfWidth, -halfHeight],
    [halfWidth, halfHeight],
    [-halfWidth, halfHeight],
  ].map(([offsetX, offsetY]) => ({
    x: centerX + offsetX * cos - offsetY * sin,
    y: centerY + offsetX * sin + offsetY * cos,
  }));
};

const boxFitsArtboard = ({
  x,
  y,
  width,
  height,
  rotation = 0,
  artboardWidth,
  artboardHeight,
}) => {
  const boxWidth = Math.max(0, Number(width) || 0);
  const boxHeight = Math.max(0, Number(height) || 0);
  if (boxWidth <= 0 || boxHeight <= 0) return false;
  const maxWidth = Math.max(1, Number(artboardWidth) || 1);
  const maxHeight = Math.max(1, Number(artboardHeight) || 1);
  return getRotatedBoxCorners({
    x,
    y,
    width: boxWidth,
    height: boxHeight,
    rotation,
  }).every(
    (corner) =>
      corner.x >= 0 &&
      corner.y >= 0 &&
      corner.x <= maxWidth &&
      corner.y <= maxHeight
  );
};

const centerBoxInArtboard = ({
  width,
  height,
  artboardWidth,
  artboardHeight,
}) => {
  const maxWidth = Math.max(1, Number(artboardWidth) || 1);
  const maxHeight = Math.max(1, Number(artboardHeight) || 1);
  const boxWidth = Math.max(1, Number(width) || 1);
  const boxHeight = Math.max(1, Number(height) || 1);
  return {
    x: Math.max(0, (maxWidth - boxWidth) / 2),
    y: Math.max(0, (maxHeight - boxHeight) / 2),
    width: boxWidth,
    height: boxHeight,
  };
};

const shiftRotatedBoxIntoArtboard = ({
  x,
  y,
  width,
  height,
  rotation = 0,
  artboardWidth,
  artboardHeight,
}) => {
  const maxWidth = Math.max(1, Number(artboardWidth) || 1);
  const maxHeight = Math.max(1, Number(artboardHeight) || 1);
  const boxWidth = Math.max(1, Number(width) || 1);
  const boxHeight = Math.max(1, Number(height) || 1);
  let nextX = Number(x) || 0;
  let nextY = Number(y) || 0;

  for (let attempt = 0; attempt < 4; attempt += 1) {
    const corners = getRotatedBoxCorners({
      x: nextX,
      y: nextY,
      width: boxWidth,
      height: boxHeight,
      rotation,
    });
    const minX = Math.min(...corners.map((corner) => corner.x));
    const maxX = Math.max(...corners.map((corner) => corner.x));
    const minY = Math.min(...corners.map((corner) => corner.y));
    const maxY = Math.max(...corners.map((corner) => corner.y));

    if (minX >= 0 && maxX <= maxWidth && minY >= 0 && maxY <= maxHeight) {
      return {
        x: nextX,
        y: nextY,
        width: boxWidth,
        height: boxHeight,
      };
    }

    if (minX < 0) nextX += -minX;
    else if (maxX > maxWidth) nextX -= maxX - maxWidth;

    if (minY < 0) nextY += -minY;
    else if (maxY > maxHeight) nextY -= maxY - maxHeight;
  }

  return centerBoxInArtboard({
    width: boxWidth,
    height: boxHeight,
    artboardWidth: maxWidth,
    artboardHeight: maxHeight,
  });
};

const isRightAngleRotation = (rotation) => {
  const degrees = (((Number(rotation) || 0) % 360) + 360) % 360;
  return Math.abs(degrees - 90) < 0.01 || Math.abs(degrees - 270) < 0.01;
};

const isAxisAlignedRotation = (rotation) => {
  const degrees = (((Number(rotation) || 0) % 180) + 180) % 180;
  return degrees < 0.01 || degrees > 179.99;
};

const resolveTextPasteSlack = ({ orientation, rotation }) => {
  if (isRightAngleRotation(rotation)) {
    return { width: PASTE_TEXT_BOX_SLACK_PX, height: PASTE_TEXT_BOX_SLACK_PX };
  }
  if (isVerticalTextOrientation(orientation)) {
    return { width: 0, height: PASTE_TEXT_BOX_SLACK_PX };
  }
  return { width: PASTE_TEXT_BOX_SLACK_PX, height: 0 };
};

const readPasteSourceBox = ({ payload, fallbackWidth, fallbackHeight }) => {
  const artboardWidth = Math.max(
    1,
    Number(payload?.artboardWidth) || fallbackWidth
  );
  const artboardHeight = Math.max(
    1,
    Number(payload?.artboardHeight) || fallbackHeight
  );
  const abs = payload?.abs || {};
  const readPx = (px, normalized, span) => {
    const value = Number(px);
    if (Number.isFinite(value)) return value;
    return (Number(normalized) || 0) * span;
  };
  return {
    artboardWidth,
    artboardHeight,
    x: readPx(abs.x, abs.nx, artboardWidth),
    y: readPx(abs.y, abs.ny, artboardHeight),
    width: Math.max(1, readPx(abs.width, abs.nw, artboardWidth)),
    height: Math.max(1, readPx(abs.height, abs.nh, artboardHeight)),
    rotation: Number(abs.rotation) || 0,
  };
};

// Keeps the ratio of free space before/after the box, so edge-anchored layers
// keep hugging their edge and centered layers stay centered.
const placeAxisForPaste = ({
  start,
  size,
  sourceSpan,
  nextSize,
  targetSpan,
}) => {
  const sourceFree = sourceSpan - size;
  const targetFree = targetSpan - nextSize;
  if (sourceFree > 1 && targetFree >= 0) {
    return (start / sourceFree) * targetFree;
  }
  const center = ((start + size / 2) / sourceSpan) * targetSpan;
  return center - nextSize / 2;
};

const resolvePasteScale = ({ payload, artboardWidth, artboardHeight }) => {
  const targetWidth = Math.max(1, Number(artboardWidth) || 1);
  const targetHeight = Math.max(1, Number(artboardHeight) || 1);
  const source = readPasteSourceBox({
    payload,
    fallbackWidth: targetWidth,
    fallbackHeight: targetHeight,
  });
  const scaleW = targetWidth / source.artboardWidth;
  const scaleH = targetHeight / source.artboardHeight;
  return { scaleW, scaleH, uniformScale: Math.min(scaleW, scaleH) };
};

export const resolvePasteOverlayPlacement = ({
  payload,
  node,
  artboardWidth,
  artboardHeight,
}) => {
  const targetWidth = Math.max(1, Number(artboardWidth) || 1);
  const targetHeight = Math.max(1, Number(artboardHeight) || 1);
  const source = readPasteSourceBox({
    payload,
    fallbackWidth: targetWidth,
    fallbackHeight: targetHeight,
  });
  const { rotation } = source;
  const { scaleW, scaleH, uniformScale } = resolvePasteScale({
    payload,
    artboardWidth: targetWidth,
    artboardHeight: targetHeight,
  });
  const stretchAxes = node?.type === "shape" && isAxisAlignedRotation(rotation);

  let width = scaleBoxPxForPaste({
    value: source.width,
    scale: stretchAxes ? scaleW : uniformScale,
  });
  let height = scaleBoxPxForPaste({
    value: source.height,
    scale: stretchAxes ? scaleH : uniformScale,
  });
  if (node?.type === "text" && Math.abs(uniformScale - 1) > 0.0001) {
    const slack = resolveTextPasteSlack({
      orientation: node.style?.orientation,
      rotation,
    });
    width += slack.width;
    height += slack.height;
  }

  let x = placeAxisForPaste({
    start: source.x,
    size: source.width,
    sourceSpan: source.artboardWidth,
    nextSize: width,
    targetSpan: targetWidth,
  });
  let y = placeAxisForPaste({
    start: source.y,
    size: source.height,
    sourceSpan: source.artboardHeight,
    nextSize: height,
    targetSpan: targetHeight,
  });

  const fitsSource = boxFitsArtboard({
    x: source.x,
    y: source.y,
    width: source.width,
    height: source.height,
    rotation,
    artboardWidth: source.artboardWidth,
    artboardHeight: source.artboardHeight,
  });
  if (
    fitsSource &&
    !boxFitsArtboard({
      x,
      y,
      width,
      height,
      rotation,
      artboardWidth: targetWidth,
      artboardHeight: targetHeight,
    })
  ) {
    ({ x, y, width, height } = shiftRotatedBoxIntoArtboard({
      x,
      y,
      width,
      height,
      rotation,
      artboardWidth: targetWidth,
      artboardHeight: targetHeight,
    }));
  }

  return {
    nx: x / targetWidth,
    ny: y / targetHeight,
    nw: width / targetWidth,
    nh: height / targetHeight,
    rotation,
  };
};

export const insertNodeAtOverlay = ({
  nodes,
  nodeLayouts,
  parentID,
  node,
  nx,
  ny,
  nw,
  nh,
  rotation = 0,
  artboardWidth,
  artboardHeight,
}) => {
  if (countNodes({ nodes }) >= CREATIVE_TEMPLATE_MAX_NODES) {
    return { nodes, nodeLayouts, ok: false };
  }
  const abs = pxBox({
    nx,
    ny,
    nw,
    nh,
    width: artboardWidth,
    height: artboardHeight,
  });
  const parent = parentID
    ? nodes[parentID] || findNodePath({ nodes, nodeID: parentID })?.node
    : null;
  if (parent?.type === "group") {
    const parentBox = getAbsolutePx({
      nodes,
      nodeLayouts,
      nodeID: parent.id,
      artboardWidth,
      artboardHeight,
    });
    const child = {
      ...node,
      layout: {
        x: abs.x - (parentBox?.x || 0),
        y: abs.y - (parentBox?.y || 0),
        width: abs.width,
        height: abs.height,
        zIndex: nextGroupInsertZIndex(parent.children),
        visible: true,
        rotation: Number(rotation) || 0,
      },
    };
    const children = { ...(parent.children || {}), [node.id]: child };
    return {
      ok: true,
      nodeLayouts,
      nodes: patchNodeById({
        nodes,
        nodeID: parent.id,
        patch: {
          children,
          childOrder: [
            ...normalizeChildOrder({
              childOrder: parent.childOrder,
              children: parent.children,
            }),
            node.id,
          ],
        },
      }),
    };
  }
  return {
    ok: true,
    nodes: { ...nodes, [node.id]: node },
    nodeLayouts: {
      ...nodeLayouts,
      [node.id]: {
        ...abs,
        zIndex: nextRootInsertZIndex(nodeLayouts),
        visible: true,
        rotation: Number(rotation) || 0,
      },
    },
  };
};

const PASTE_TEXT_BOX_SLACK_PX = 2;

const scaleMetricForPaste = ({ value, scale, mode = "round" }) => {
  const scaled = (Number(value) || 0) * scale;
  if (!(scaled > 0)) return 0;
  if (mode === "floor") return Math.max(1, Math.floor(scaled));
  if (mode === "ceil") return Math.max(1, Math.ceil(scaled));
  return Math.max(1, Math.round(scaled));
};

const scalePxForPaste = ({ value, scale }) =>
  scaleMetricForPaste({ value, scale, mode: "round" });

const scaleFontSizeForPaste = ({ value, scale }) =>
  scaleMetricForPaste({
    value,
    scale,
    mode: scale < 1 ? "floor" : "round",
  });

const scaleBoxPxForPaste = ({ value, scale }) =>
  scaleMetricForPaste({
    value,
    scale,
    mode: scale < 1 ? "ceil" : "round",
  });

const scaleTextNodeMetricsForPaste = ({ node, scale }) => {
  const next = { ...node };
  const hasHighlightMetrics = Array.isArray(node.style?.highlights)
    ? node.style.highlights.some(
        (slot) =>
          slot?.strokeWidth != null ||
          slot?.backgroundPadding != null ||
          slot?.backgroundRadius != null
      )
    : false;
  if (
    node.style?.fontSize != null ||
    node.style?.letterSpacing != null ||
    node.style?.backgroundPadding != null ||
    node.style?.backgroundRadius != null ||
    node.style?.strokeWidth != null ||
    hasHighlightMetrics
  ) {
    next.style = { ...node.style };
    if (node.style?.fontSize != null) {
      next.style.fontSize = Math.max(
        1,
        scaleFontSizeForPaste({ value: node.style.fontSize, scale })
      );
    }
    if (node.style?.letterSpacing != null) {
      const spacing = Number(node.style.letterSpacing);
      next.style.letterSpacing = Number.isFinite(spacing)
        ? scalePxForPaste({ value: spacing, scale })
        : node.style.letterSpacing;
    }
    if (node.style?.backgroundPadding != null) {
      const padding = Number(node.style.backgroundPadding);
      next.style.backgroundPadding = Number.isFinite(padding)
        ? scalePxForPaste({ value: padding, scale })
        : node.style.backgroundPadding;
    }
    if (node.style?.backgroundRadius != null) {
      const radius = Number(node.style.backgroundRadius);
      next.style.backgroundRadius = Number.isFinite(radius)
        ? scalePxForPaste({ value: radius, scale })
        : node.style.backgroundRadius;
    }
    if (node.style?.strokeWidth != null) {
      const strokeWidth = Number(node.style.strokeWidth);
      next.style.strokeWidth = Number.isFinite(strokeWidth)
        ? scalePxForPaste({ value: strokeWidth, scale })
        : node.style.strokeWidth;
    }
    if (Array.isArray(node.style?.highlights) && node.style.highlights.length) {
      next.style.highlights = scaleHighlightMetrics({
        highlights: node.style.highlights,
        scale,
      });
    }
  }
  if (node.overflow?.minimumFontSize != null) {
    next.overflow = {
      ...node.overflow,
      minimumFontSize: Math.max(
        1,
        scalePxForPaste({ value: node.overflow.minimumFontSize, scale })
      ),
    };
  }
  return next;
};

const scaleChildLayoutForPaste = ({ layout, scale }) => {
  if (!layout) return layout;
  return {
    ...layout,
    x: Math.round((Number(layout.x) || 0) * scale),
    y: Math.round((Number(layout.y) || 0) * scale),
    width: scaleBoxPxForPaste({ value: layout.width, scale }),
    height: scaleBoxPxForPaste({ value: layout.height, scale }),
  };
};

const scalePastedNodeMetrics = ({ node, scale }) => {
  if (!node || Math.abs(scale - 1) < 0.0001) return node;

  const scaleTree = (current, { isRoot }) => {
    if (!current) return current;
    const next =
      current.type === "text"
        ? scaleTextNodeMetricsForPaste({ node: current, scale })
        : { ...current };
    if (!isRoot && current.layout) {
      next.layout = scaleChildLayoutForPaste({ layout: current.layout, scale });
    }
    if (current.shape?.radius != null) {
      next.shape = {
        ...current.shape,
        radius: scalePxForPaste({ value: current.shape.radius, scale }),
      };
    }
    if (current.componentFrame) {
      next.componentFrame = {
        ...current.componentFrame,
        width: scaleBoxPxForPaste({
          value: current.componentFrame.width,
          scale,
        }),
        height: scaleBoxPxForPaste({
          value: current.componentFrame.height,
          scale,
        }),
      };
    }
    if (current.children) {
      const children = {};
      Object.entries(current.children).forEach(([childID, child]) => {
        if (!child) return;
        children[childID] = scaleTree(child, { isRoot: false });
      });
      next.children = children;
    }
    return next;
  };

  return scaleTree(node, { isRoot: true });
};

const cloneNode = ({ node, createID, idMap }) => {
  const newID = createID();
  idMap[node.id] = newID;
  const sourceChildren = node.children || {};
  const children = {};
  const childOrder = normalizeChildOrder({
    childOrder: node.childOrder,
    children: sourceChildren,
  }).flatMap((childID) => {
    const child = sourceChildren[childID];
    if (!child) return [];
    const cloned = cloneNode({
      node: child,
      createID,
      idMap,
    });
    children[cloned.id] = cloned;
    return [cloned.id];
  });
  return {
    ...node,
    id: newID,
    children: node.children ? children : undefined,
    childOrder: node.children ? childOrder : undefined,
  };
};

const addAssetRef = (refs, value) => {
  const key = String(value || "").trim();
  if (key) refs.add(key);
};

const listSourceReferenceAssets = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const collectAssetRefsFromNode = (node, refs = new Set()) => {
  addAssetRef(refs, node?.source?.assetRef);
  addAssetRef(refs, node?.originAssetRef);
  listSourceReferenceAssets(node?.source?.referenceAssets).forEach((ref) => {
    addAssetRef(refs, ref);
  });
  listStoredInlineReplacements(node?.style?.inlineReplacements).forEach(
    (slot) => {
      addAssetRef(refs, slot.assetRef);
    }
  );
  Object.values(node?.children || {}).forEach((child) => {
    collectAssetRefsFromNode(child, refs);
  });
  return refs;
};

const mappedAssetRef = (value, keyMap) => {
  const key = String(value || "").trim();
  if (!key) return value;
  return keyMap[key] || key;
};

const remapReferenceAssets = (value, keyMap) => {
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((ref) => {
      const mapped = mappedAssetRef(ref, keyMap);
      if (mapped !== ref) changed = true;
      return mapped;
    });
    return changed ? next : value;
  }
  if (value && typeof value === "object") {
    let changed = false;
    const next = {};
    Object.entries(value).forEach(([entryKey, ref]) => {
      const mapped = mappedAssetRef(ref, keyMap);
      if (mapped !== ref) changed = true;
      next[entryKey] = mapped;
    });
    return changed ? next : value;
  }
  return value;
};

const remapAssetRefsInNode = (node, keyMap) => {
  if (!node) return node;
  const currentRef = node.source?.assetRef;
  const mappedSourceRef = currentRef
    ? mappedAssetRef(currentRef, keyMap)
    : null;
  let nextSource = node.source;
  if (mappedSourceRef && mappedSourceRef !== currentRef) {
    nextSource = { ...nextSource, assetRef: mappedSourceRef };
  }
  if (nextSource?.referenceAssets) {
    const nextRefs = remapReferenceAssets(nextSource.referenceAssets, keyMap);
    if (nextRefs !== nextSource.referenceAssets) {
      nextSource = { ...nextSource, referenceAssets: nextRefs };
    }
  }
  const originAssetRef = String(node.originAssetRef || "").trim();
  const mappedOrigin = originAssetRef
    ? mappedAssetRef(originAssetRef, keyMap)
    : node.originAssetRef;
  const replacements = listStoredInlineReplacements(
    node.style?.inlineReplacements
  );
  const nextReplacements = replacements.map((slot) => {
    const nextRef = mappedAssetRef(slot.assetRef, keyMap);
    if (nextRef === slot.assetRef) return slot;
    return { ...slot, assetRef: nextRef };
  });
  const replacementsChanged = nextReplacements.some(
    (slot, index) => slot !== replacements[index]
  );
  const children = node.children
    ? Object.fromEntries(
        Object.entries(node.children).map(([id, child]) => [
          id,
          remapAssetRefsInNode(child, keyMap),
        ])
      )
    : node.children;
  const childrenChanged =
    Boolean(node.children) &&
    Object.keys(node.children).some((id) => children[id] !== node.children[id]);
  const originChanged = mappedOrigin !== node.originAssetRef;
  if (
    nextSource === node.source &&
    !originChanged &&
    !replacementsChanged &&
    !childrenChanged
  ) {
    return node;
  }
  const next = { ...node, source: nextSource };
  if (originChanged) next.originAssetRef = mappedOrigin;
  if (replacementsChanged) {
    next.style = {
      ...(node.style || {}),
      inlineReplacements: nextReplacements,
    };
  }
  if (childrenChanged) next.children = children;
  return next;
};

const mergeClipboardAssets = ({ assets = {}, clipboardAssets = {}, node }) => {
  const keyMap = {};
  let nextAssets = { ...assets };
  Object.entries(clipboardAssets || {}).forEach(([key, asset]) => {
    const existing = nextAssets[key];
    if (existing && existing.url === asset?.url) {
      keyMap[key] = key;
      return;
    }
    if (!existing) {
      nextAssets = { ...nextAssets, [key]: structuredClone(asset) };
      keyMap[key] = key;
      return;
    }
    const nextKey = uniqueName({
      name: key,
      existingNames: Object.keys(nextAssets),
    });
    nextAssets = { ...nextAssets, [nextKey]: structuredClone(asset) };
    keyMap[key] = nextKey;
  });
  return {
    assets: nextAssets,
    node: remapAssetRefsInNode(node, keyMap),
  };
};

export const copyNodeById = ({
  nodes = {},
  nodeLayouts = {},
  assets = {},
  nodeID,
  artboardWidth,
  artboardHeight,
}) => {
  const found = findNodePath({ nodes, nodeID });
  if (!found) return null;
  if (isCanvasBackgroundNode({ node: found.node, parentID: found.parentID })) {
    return null;
  }
  const sourceWidth = Math.max(1, Number(artboardWidth) || 1);
  const sourceHeight = Math.max(1, Number(artboardHeight) || 1);
  const abs = getAbsolutePx({
    nodes,
    nodeLayouts,
    nodeID,
    artboardWidth: sourceWidth,
    artboardHeight: sourceHeight,
  });
  const copiedAssets = {};
  collectAssetRefsFromNode(found.node).forEach((key) => {
    if (assets[key]) {
      copiedAssets[key] = structuredClone(assets[key]);
    }
  });
  const x = Number(abs?.x) || 0;
  const y = Number(abs?.y) || 0;
  const width = Number(abs?.width) || 0;
  const height = Number(abs?.height) || 0;
  return {
    kind: CANVAS_CLIPBOARD_KIND.TEMPLATE_NODE,
    node: structuredClone(found.node),
    artboardWidth: sourceWidth,
    artboardHeight: sourceHeight,
    abs: {
      x,
      y,
      width,
      height,
      nx: x / sourceWidth,
      ny: y / sourceHeight,
      nw: width / sourceWidth,
      nh: height / sourceHeight,
      rotation: Number(abs?.rotation) || 0,
    },
    assets: copiedAssets,
  };
};

export const pasteCopiedNode = ({
  nodes = {},
  nodeLayouts = {},
  assets = {},
  payload,
  selectedID,
  createID,
  artboardWidth,
  artboardHeight,
}) => {
  if (
    payload?.kind !== CANVAS_CLIPBOARD_KIND.TEMPLATE_NODE ||
    !payload?.node ||
    typeof createID !== "function"
  ) {
    return { nodes, nodeLayouts, assets, ok: false };
  }
  const width = Math.max(1, Number(artboardWidth) || 1);
  const height = Math.max(1, Number(artboardHeight) || 1);
  const cloned = cloneNode({
    node: payload.node,
    createID,
    idMap: {},
  });
  cloned.name = uniqueName({
    name: payload.node.name,
    existingNames: listNodeNames({ nodes }),
  });
  if (cloned.isBackground) {
    delete cloned.isBackground;
    if (cloned.type === "image") cloned.fit = "fill";
  }
  const merged = mergeClipboardAssets({
    assets,
    clipboardAssets: payload.assets,
    node: cloned,
  });
  const placement = resolvePasteOverlayPlacement({
    payload,
    node: merged.node,
    artboardWidth: width,
    artboardHeight: height,
  });
  const { uniformScale } = resolvePasteScale({
    payload,
    artboardWidth: width,
    artboardHeight: height,
  });
  const scaledNode = scalePastedNodeMetrics({
    node: merged.node,
    scale: uniformScale,
  });
  const result = insertNodeAtOverlay({
    nodes,
    nodeLayouts,
    parentID: resolveInsertParentID({ nodes, selectedID }),
    node: scaledNode,
    nx: placement.nx,
    ny: placement.ny,
    nw: placement.nw,
    nh: placement.nh,
    rotation: placement.rotation,
    artboardWidth: width,
    artboardHeight: height,
  });
  return {
    ...result,
    assets: merged.assets,
    pastedID: merged.node.id,
  };
};

/**
 * Copies one or many nodes. A multi-node payload keeps the first node in the
 * regular payload fields (backwards compatible) and lists every copy in `items`.
 */
export const copyNodesByIds = ({ nodeIDs = [], ...rest }) => {
  const selected = new Set((nodeIDs || []).filter(Boolean));
  const hasSelectedAncestor = (nodeID) => {
    let parentID = findNodePath({ nodes: rest.nodes, nodeID })?.parentID;
    while (parentID) {
      if (selected.has(parentID)) return true;
      parentID = findNodePath({
        nodes: rest.nodes,
        nodeID: parentID,
      })?.parentID;
    }
    return false;
  };
  const uniqueIDs = [...selected].filter(
    (nodeID) => !hasSelectedAncestor(nodeID)
  );
  const payloads = uniqueIDs
    .map((nodeID) => copyNodeById({ ...rest, nodeID }))
    .filter(Boolean);
  if (payloads.length === 0) return null;
  if (payloads.length === 1) return payloads[0];
  return { ...payloads[0], items: payloads };
};

export const pasteCopiedNodes = ({
  nodes = {},
  nodeLayouts = {},
  assets = {},
  payload,
  ...rest
}) => {
  const entries =
    Array.isArray(payload?.items) && payload.items.length > 0
      ? payload.items
      : [payload];
  let current = { nodes, nodeLayouts, assets };
  const pastedIDs = [];
  entries.forEach((entry) => {
    const result = pasteCopiedNode({ ...current, ...rest, payload: entry });
    if (!result.ok) return;
    current = {
      nodes: result.nodes,
      nodeLayouts: result.nodeLayouts,
      assets: result.assets,
    };
    pastedIDs.push(result.pastedID);
  });
  if (pastedIDs.length === 0) {
    return { nodes, nodeLayouts, assets, ok: false };
  }
  return {
    ...current,
    ok: true,
    pastedID: pastedIDs[pastedIDs.length - 1],
    pastedIDs,
  };
};

export const groupSelectedNodes = ({
  nodes = {},
  nodeLayouts = {},
  nodeIDs = [],
  createID,
  groupName = "Group",
}) => {
  const uniqueIDs = [...new Set((nodeIDs || []).filter(Boolean))];
  if (uniqueIDs.length < 2 || typeof createID !== "function") {
    return { nodes, nodeLayouts, ok: false, groupID: null };
  }

  const foundItems = uniqueIDs.map((nodeID) => ({
    nodeID,
    found: findNodePath({ nodes, nodeID }),
  }));
  if (foundItems.some((item) => !item.found)) {
    return { nodes, nodeLayouts, ok: false, groupID: null };
  }

  const groupable = foundItems.filter(
    ({ found }) =>
      !isCanvasBackgroundNode({
        node: found.node,
        parentID: found.parentID,
      })
  );
  if (groupable.length < 2) {
    return { nodes, nodeLayouts, ok: false, groupID: null };
  }

  const parentID = groupable[0].found.parentID || null;
  const sameParent = groupable.every(
    (item) => (item.found.parentID || null) === parentID
  );
  if (!sameParent) {
    return { nodes, nodeLayouts, ok: false, groupID: null };
  }

  const boxes = groupable.map(({ nodeID, found }) => {
    const abs = getAbsolutePx({
      nodes,
      nodeLayouts,
      nodeID,
      artboardWidth: 1,
      artboardHeight: 1,
    });
    return { nodeID, found, abs };
  });
  const minX = Math.min(...boxes.map((box) => Number(box.abs?.x) || 0));
  const minY = Math.min(...boxes.map((box) => Number(box.abs?.y) || 0));
  const maxX = Math.max(
    ...boxes.map(
      (box) => (Number(box.abs?.x) || 0) + (Number(box.abs?.width) || 0)
    )
  );
  const maxY = Math.max(
    ...boxes.map(
      (box) => (Number(box.abs?.y) || 0) + (Number(box.abs?.height) || 0)
    )
  );
  const groupWidth = Math.max(1, maxX - minX);
  const groupHeight = Math.max(1, maxY - minY);

  const groupID = createID();
  const children = {};
  const childOrder = [];
  boxes.forEach(({ nodeID, found, abs }, index) => {
    const child = structuredClone(found.node);
    child.layout = {
      x: (Number(abs?.x) || 0) - minX,
      y: (Number(abs?.y) || 0) - minY,
      width: Math.max(1, Number(abs?.width) || 0),
      height: Math.max(1, Number(abs?.height) || 0),
      zIndex: index + 1,
      visible: abs?.visible !== false,
    };
    children[nodeID] = child;
    childOrder.push(nodeID);
  });

  const groupNode = createDefaultNode({
    id: groupID,
    name: groupName,
    existingNames: listNodeNames({ nodes }),
    type: "group",
  });
  groupNode.children = children;
  groupNode.childOrder = childOrder;
  groupNode.componentFrame = { width: groupWidth, height: groupHeight };

  let nextNodes = nodes;
  let nextLayouts = nodeLayouts;
  boxes.forEach(({ nodeID }) => {
    const deleted = deleteNodeById({
      nodes: nextNodes,
      nodeLayouts: nextLayouts,
      nodeID,
    });
    nextNodes = deleted.nodes;
    nextLayouts = deleted.nodeLayouts;
  });

  if (!parentID) {
    return {
      nodes: { ...nextNodes, [groupID]: groupNode },
      nodeLayouts: {
        ...nextLayouts,
        [groupID]: {
          x: minX,
          y: minY,
          width: groupWidth,
          height: groupHeight,
          zIndex: nextRootInsertZIndex(nextLayouts),
          visible: true,
          rotation: 0,
        },
      },
      ok: true,
      groupID,
    };
  }

  const parent = findNodePath({ nodes: nextNodes, nodeID: parentID })?.node;
  if (!parent) {
    return { nodes, nodeLayouts, ok: false, groupID: null };
  }
  const parentBox = getAbsolutePx({
    nodes: nextNodes,
    nodeLayouts: nextLayouts,
    nodeID: parentID,
    artboardWidth: 1,
    artboardHeight: 1,
  });
  const childGroup = {
    ...groupNode,
    layout: {
      x: minX - (parentBox?.x || 0),
      y: minY - (parentBox?.y || 0),
      width: groupWidth,
      height: groupHeight,
      zIndex: nextGroupInsertZIndex(parent.children),
      visible: true,
    },
  };
  const parentChildren = {
    ...(parent.children || {}),
    [groupID]: childGroup,
  };
  return {
    nodes: patchNodeById({
      nodes: nextNodes,
      nodeID: parentID,
      patch: {
        children: parentChildren,
        childOrder: [
          ...normalizeChildOrder({
            childOrder: parent.childOrder,
            children: parent.children,
          }),
          groupID,
        ],
      },
    }),
    nodeLayouts: nextLayouts,
    ok: true,
    groupID,
  };
};

export const duplicateNodeById = ({ nodes, nodeLayouts, nodeID, createID }) => {
  const found = findNodePath({ nodes, nodeID });
  if (!found) return { nodes, nodeLayouts };
  const cloned = cloneNode({ node: found.node, createID, idMap: {} });
  cloned.name = uniqueName({
    name: found.node.name,
    existingNames: listNodeNames({ nodes }),
  });
  if (!found.parentID) {
    const layout = nodeLayouts[nodeID] || {};
    return {
      nodes: { ...nodes, [cloned.id]: cloned },
      nodeLayouts: {
        ...nodeLayouts,
        [cloned.id]: {
          ...layout,
          x: (layout.x || 0) + 16,
          y: (layout.y || 0) + 16,
        },
      },
    };
  }
  const parent = findNodePath({ nodes, nodeID: found.parentID })?.node;
  const children = { ...(parent.children || {}), [cloned.id]: cloned };
  return {
    nodeLayouts,
    nodes: patchNodeById({
      nodes,
      nodeID: parent.id,
      patch: {
        children,
        childOrder: [
          ...normalizeChildOrder({
            childOrder: parent.childOrder,
            children: parent.children,
          }),
          cloned.id,
        ],
      },
    }),
  };
};

export const setNodeVisible = ({ nodes, nodeLayouts, nodeID, visible }) => {
  const found = findNodePath({ nodes, nodeID });
  if (!found) return { nodes, nodeLayouts };
  if (!found.parentID) {
    return {
      nodes,
      nodeLayouts: {
        ...nodeLayouts,
        [nodeID]: { ...(nodeLayouts[nodeID] || {}), visible },
      },
    };
  }
  return {
    nodeLayouts,
    nodes: patchNodeById({
      nodes,
      nodeID,
      patch: {
        layout: { ...(found.node.layout || {}), visible },
      },
    }),
  };
};

export const isNodeVisible = ({ nodes, nodeLayouts, nodeID }) => {
  const found = findNodePath({ nodes, nodeID });
  if (!found) return true;
  if (!found.parentID) return nodeLayouts[nodeID]?.visible !== false;
  return found.node.layout?.visible !== false;
};

const sanitizeNode = ({ node, id, isRoot }) => {
  if (!node) return null;
  const clean = { ...node, id: node.id || id };
  if (isRoot) delete clean.layout;
  if (clean.type === "group" || clean.type === "shape") delete clean.source;
  if (clean.source?.kind === "data") {
    clean.source = {
      ...clean.source,
      variable: slugifyName({ name: clean.name, fallback: clean.id }),
    };
  }
  if (clean.source?.kind === "generated") {
    const nextSource = { ...clean.source };
    delete nextSource.negativePrompt;
    clean.source = {
      ...nextSource,
      generator: clean.type === "text" ? "text" : "image",
    };
  }
  if (clean.type !== "group") {
    delete clean.children;
    delete clean.childOrder;
    delete clean.componentFrame;
    delete clean.clipContent;
  } else if (clean.children) {
    clean.children = sanitizeNodesForPersist({
      nodes: clean.children,
      isRoot: false,
    });
  }
  if (clean.type !== "text") {
    delete clean.style;
    delete clean.overflow;
  } else {
    clean.overflow = {
      ...(clean.overflow || {}),
      strategy: "shrink",
    };
  }
  if (clean.type === "image") {
    const keepBackground = isCanvasBackgroundNode({
      node: clean,
      parentID: isRoot ? null : clean.id,
    });
    if (keepBackground) {
      clean.isBackground = true;
      clean.fit = canvasBackgroundFit(clean);
    } else {
      delete clean.isBackground;
      clean.fit = "fill";
    }
    const nextCrop = persistableCrop(clean.crop);
    if (nextCrop) clean.crop = nextCrop;
    else delete clean.crop;
    if (clean.constraints) {
      const nextConstraints = { ...clean.constraints };
      delete nextConstraints.minimumWidth;
      delete nextConstraints.safeArea;
      clean.constraints = Object.keys(nextConstraints).length
        ? nextConstraints
        : undefined;
    }
  } else {
    delete clean.fit;
    delete clean.isBackground;
    delete clean.crop;
  }
  if (clean.type !== "shape") delete clean.shape;
  if (clean.type !== "image") delete clean.originAssetRef;
  if (clean.type !== "text" || !String(clean.originText || "")) {
    delete clean.originText;
  }
  return omitUndefinedDeep(clean);
};

export const sanitizeNodesForPersist = ({ nodes = {}, isRoot = true }) => {
  const next = {};
  Object.entries(nodes).forEach(([id, node]) => {
    const clean = sanitizeNode({ node, id, isRoot });
    if (clean) next[id] = clean;
  });
  return next;
};

export const sanitizeGraphForPersist = ({ nodes = {}, nodeLayouts = {} }) => {
  const cleanNodes = sanitizeNodesForPersist({ nodes, isRoot: true });
  const cleanLayouts = {};
  Object.entries(nodeLayouts || {}).forEach(([id, layout]) => {
    if (cleanNodes[id]) cleanLayouts[id] = layout;
  });
  return { nodes: cleanNodes, nodeLayouts: cleanLayouts };
};

export const sanitizeArtboardsForPersist = ({ artboards = {} }) => {
  const nextArtboards = {};
  Object.entries(artboards || {}).forEach(([id, artboard]) => {
    const cleanNodes = sanitizeNodesForPersist({
      nodes: artboard?.nodes || {},
      isRoot: true,
    });
    const cleanLayouts = {};
    Object.entries(artboard?.nodeLayouts || {}).forEach(
      ([layoutID, layout]) => {
        if (cleanNodes[layoutID]) cleanLayouts[layoutID] = layout;
      }
    );
    nextArtboards[id] = {
      ...artboard,
      nodes: cleanNodes,
      nodeLayouts: cleanLayouts,
    };
  });
  return { artboards: nextArtboards };
};
